import {useEffect, useMemo} from 'react';

import {getPlateMapPositionInput} from 'samples/plate-maps/AnnotateFillPlateUtil';
import {PlateMap} from 'samples/plate-maps/PlateMapConstants';
import {hydrateReagentErrorsFromValidation} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {getErrorsByLayerAndLevel} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {validatePlateMap, ValidatePlateMapParams} from 'samples/plate-maps/validation/ValidationDAO';
import {
  PlateMapValidationResponse,
  ValidationErrorsByLayerAndLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import useAsyncState, {AsyncStatus} from 'ui-library/hooks/useAsyncState';

export function usePlateMapValidation({
  plateId,
  plateMap,
  annotatedDataParameterIds,
  annotatedSchemaFieldIds,
  plateSchemaId,
  documentId,
  includeMeasurementLayers,
}: {
  plateId: string | null;
  plateMap: PlateMap | null;
  annotatedDataParameterIds?: Set<string>;
  annotatedSchemaFieldIds?: Set<string>;
  plateSchemaId: string | null;
  documentId: string | null;
  includeMeasurementLayers: boolean;
}): {
  refetch: (() => void) | null;
  validationStatus: AsyncStatus;
  validationRequestFailedMessage: string | null;
  validatedPlateMap: PlateMap | null;
  validationErrors: ValidationErrorsByLayerAndLevel | null;
} {
  const validatePlateMapParams: ValidatePlateMapParams | null = useMemo(
    () =>
      plateMap == null
        ? null
        : {
            positionInputs: plateMap.wells.map((well) =>
              getPlateMapPositionInput(
                well,
                annotatedDataParameterIds ?? new Set(),
                annotatedSchemaFieldIds ?? new Set(),
                includeMeasurementLayers
              )
            ),
            plateId,
            plateSchemaId,
            documentId,
          },
    [
      plateMap,
      plateId,
      annotatedDataParameterIds,
      annotatedSchemaFieldIds,
      plateSchemaId,
      documentId,
      includeMeasurementLayers,
    ]
  );

  const {currentState, makeCall} = useAsyncState<[ValidatePlateMapParams], PlateMapValidationResponse>({
    makeCall: validatePlateMap,
    includeInput: true,
    keepOnlyLatest: true,
  });

  useEffect(() => {
    if (validatePlateMapParams != null) {
      makeCall(validatePlateMapParams);
    }
  }, [makeCall, validatePlateMapParams]);

  const validatedPlateMap = useMemo(() => {
    if (plateMap != null) {
      if (currentState.status === AsyncStatus.SUCCESS) {
        return hydrateReagentErrorsFromValidation(plateMap, currentState.data);
      } else if (currentState.status === AsyncStatus.LOADING && currentState.previousData != null) {
        return hydrateReagentErrorsFromValidation(plateMap, currentState.previousData);
      }
    }
    return null;
  }, [currentState, plateMap]);

  const validationErrors = useMemo(() => {
    if (currentState.status === AsyncStatus.SUCCESS) {
      return getErrorsByLayerAndLevel(currentState.data);
    } else if (currentState.status === AsyncStatus.LOADING && currentState.previousData != null) {
      return getErrorsByLayerAndLevel(currentState.previousData);
    }
    return null;
  }, [currentState]);

  return {
    refetch:
      validatePlateMapParams != null
        ? () => {
            makeCall(validatePlateMapParams);
          }
        : null,
    validationStatus: currentState.status,
    validationRequestFailedMessage: currentState.status === AsyncStatus.ERROR ? currentState.message : null,
    validatedPlateMap,
    validationErrors,
  };
}
